import {
  IconButton,
  notify,
  RevealButton,
  useLegalLinks,
} from '@affine/component';
import {
  AuthContainer,
  AuthContent,
  AuthHeader,
  AuthInput,
} from '@affine/component/auth-components';
import { OAuth } from '@affine/core/components/affine/auth/oauth';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import {
  type AccessGateMode,
  AuthService,
  CaptchaService,
  ServerService,
} from '@affine/core/modules/cloud';
import type { AuthSessionStatus } from '@affine/core/modules/cloud/entities/session';
import { UserFriendlyError } from '@affine/error';
import { ServerDeploymentType } from '@affine/graphql';
import { Trans, useI18n } from '@affine/i18n';
import { InvisibleIcon, ViewIcon } from '@blocksuite/icons/rc';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import { useLiveData, useService } from '@toeverything/infra';
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  AuthJourneyProgress,
  saveAuthJourneyPresentationMarker,
} from '../auth-journey';
import { useSelfhostLoginVersionGuard } from '../hooks/affine/use-selfhost-login-version-guard';
import type { SignInState } from '.';
import { Back } from './back';
import { Captcha } from './captcha';
import { SignInWithPasskey } from './sign-in-with-passkey';
import * as style from './style.css';

// =============================================================================
// "Last used" auth method persistence
// =============================================================================

const LAST_AUTH_KEY = 'streamlain:last-auth-method';

export type AuthMethod =
  | 'email'
  | 'google'
  | 'github'
  | 'microsoft'
  | 'oidc'
  | 'apple';

export const getLastAuthMethod = (): AuthMethod | null => {
  try {
    return (localStorage.getItem(LAST_AUTH_KEY) as AuthMethod) || null;
  } catch {
    return null;
  }
};

export const setLastAuthMethod = (method: AuthMethod): void => {
  try {
    localStorage.setItem(LAST_AUTH_KEY, method);
  } catch {
    // Ignore storage errors
  }
};

// =============================================================================

const emailRegex =
  /^(?:(?:[^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(?:(?:\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|((?:[a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

function validateEmail(email: string) {
  return emailRegex.test(email);
}

/**
 * sl-4g7d.23: Unified Sign-In page — Email + Password on one page.
 *
 * No preflight endpoint, no two-step flow. Users enter email and password
 * together. Backend returns identical 'Invalid credentials' for both
 * non-existent users and wrong passwords (account enumeration prevention).
 *
 * Registration is via explicit "Register" link → Magic Link flow.
 */
export const SignInStep = ({
  state,
  changeState,
  onAuthenticated,
  planContext,
}: {
  state: SignInState;
  changeState: Dispatch<SetStateAction<SignInState>>;
  onAuthenticated?: (status: AuthSessionStatus) => void;
  planContext?: string | null;
}) => {
  const t = useI18n();
  const legalLinks = useLegalLinks();
  const serverService = useService(ServerService);
  const serverName = useLiveData(
    serverService.server.config$.selector(c => c.serverName)
  );
  const displayServerName = useMemo(() => {
    return serverName?.replace(/affine/gi, BRAND_DISPLAY_NAME);
  }, [serverName]);
  const versionError = useSelfhostLoginVersionGuard(serverService.server);
  const isSelfhosted = useLiveData(
    serverService.server.config$.selector(
      c => c.type === ServerDeploymentType.Selfhosted
    )
  );
  const authService = useService(AuthService);
  const captchaService = useService(CaptchaService);
  const lastMethod = useMemo(() => getLastAuthMethod(), []);
  const [email, setEmail] = useState(state.email ?? '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [passwordError, setPasswordError] = useState(false);
  const [accessMode, setAccessMode] = useState<AccessGateMode>();
  const [accessCode, setAccessCode] = useState('');
  const [hasCapturedAccessCode, setHasCapturedAccessCode] = useState(false);

  const verifyToken = useLiveData(captchaService.verifyToken$);
  const needCaptcha = useLiveData(captchaService.needCaptcha$);
  const [isLoading, setIsLoading] = useState(false);
  // sl-sc2d: SSO Discovery state
  const [ssoDiscovery, setSsoDiscovery] = useState<{
    hasSaml: boolean;
    workspaceId?: string;
  } | null>(null);

  const loginStatus = useLiveData(authService.session.status$);

  useEffect(() => {
    onAuthenticated?.(loginStatus);
  }, [loginStatus, onAuthenticated]);

  useEffect(() => {
    if (isSelfhosted) return;
    authService.captureAccessCodeFromLocation();
    setHasCapturedAccessCode(authService.hasAccessCode());
    void authService
      .getAccessMode()
      .then(setAccessMode)
      .catch(() => {
        // Existing sign-ins remain available; new-account creation fails closed.
      });
  }, [authService, isSelfhosted]);

  const onAccessCodeChanged = useCallback(
    (value: string) => {
      setAccessCode(value);
      authService.setAccessCode(value);
      setHasCapturedAccessCode(authService.hasAccessCode());
    },
    [authService]
  );

  // sl-sc2d: SSO Discovery — check if email domain has SAML configured (debounced 500ms)
  useEffect(() => {
    if (!email || !email.includes('@') || !validateEmail(email)) {
      setSsoDiscovery(null);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      fetch(`/api/auth/saml/discover?email=${encodeURIComponent(email)}`, {
        signal: controller.signal,
      })
        .then(res => res.json())
        .then(data => setSsoDiscovery(data))
        .catch(() => setSsoDiscovery(null));
    }, 500);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [email]);

  const onSsoLogin = useCallback(() => {
    if (ssoDiscovery?.workspaceId) {
      saveAuthJourneyPresentationMarker({
        version: 1,
        journeyId: 'sign-in',
        stageId: 'authenticate',
        startedAt: Date.now(),
      });
      window.location.href = `/api/auth/saml/login/${ssoDiscovery.workspaceId}`;
    }
  }, [ssoDiscovery]);

  // sl-4g7d.23: Direct sign-in with email + password — no preflight, no two-step flow
  const onSignIn = useAsyncCallback(async () => {
    if (!validateEmail(email)) {
      setIsValidEmail(false);
      return;
    }
    setIsValidEmail(true);

    if (!password) {
      setPasswordError(true);
      return;
    }

    if (isLoading) return;
    setIsLoading(true);
    setPasswordError(false);

    try {
      const currentVerifyToken =
        captchaService.verifyToken$.value ?? verifyToken;
      const result = await authService.signInPassword({
        email,
        password,
        verifyToken: currentVerifyToken,
      });
      setLastAuthMethod('email');

      // sl-qsjm.4: If 2FA is required, transition to TOTP step
      if (result.requiresTwoFactor) {
        changeState(prev => ({
          ...prev,
          email,
          step: 'signInWithTotp',
          preferredTwoFactorMethod: result.preferredMethod,
        }));
        return;
      }

      // sl-0aw1.3.1: Notify about workspaces requiring 2FA
      if (
        result.workspacesRequiring2FA &&
        result.workspacesRequiring2FA.length > 0
      ) {
        const count = result.workspacesRequiring2FA.length;
        notify.warning({
          title: t['com.streamlain.two-factor-guard.sign-in-toast.title'](),
          message: t['com.streamlain.two-factor-guard.sign-in-toast.message']({
            count: String(count),
          }),
        });
      }
    } catch (err) {
      const error = UserFriendlyError.fromAny(err);
      if (error.isStatus(401)) {
        setPasswordError(true);
        return;
      }

      console.error(err);
      setPasswordError(false);
      notify.error({
        title: 'Anmeldung nicht möglich',
        message:
          error.message ||
          'Der Server konnte die Anmeldung nicht verarbeiten. Bitte gleich erneut versuchen.',
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    email,
    password,
    isLoading,
    verifyToken,
    captchaService,
    authService,
    changeState,
    t,
  ]);

  // sl-4g7d.23: Explicit register action → Magic Link flow
  // Always navigate to the email step — email is pre-filled if entered,
  // but the user can always edit it on the next screen before sending the code.
  const onRegister = useCallback(() => {
    changeState(prev => ({
      ...prev,
      email: email || undefined,
      step: 'signInWithEmail',
      intent: 'registration',
    }));
  }, [changeState, email]);

  // Navigate to email verification step (for users who forgot their password)
  // Same flow as register — carries email over if entered.
  const onForgotPassword = useCallback(() => {
    changeState(prev => ({
      ...prev,
      email: email || undefined,
      step: 'signInWithEmail',
      intent: 'recovery',
    }));
  }, [changeState, email]);

  const onTogglePasswordVisibility = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setShowPassword(prev => !prev);
    },
    []
  );

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      void onSignIn();
    },
    [onSignIn]
  );

  if (versionError && isSelfhosted) {
    return (
      <AuthContainer>
        <AuthHeader
          className={style.signInHeader}
          title={t['com.streamlain.auth.sign.in']()}
          subTitle={displayServerName}
        />
        <AuthContent>
          <div>{versionError}</div>
        </AuthContent>
      </AuthContainer>
    );
  }

  // Capitalize plan name for display (e.g. "team" → "Team")
  const planDisplayName = planContext
    ? planContext.charAt(0).toUpperCase() + planContext.slice(1)
    : null;

  return (
    <AuthContainer>
      <AuthHeader
        className={style.signInHeader}
        title={t['com.streamlain.auth.sign.in']()}
        subTitle={displayServerName}
      />

      {planDisplayName && (
        <div className={style.planContextBanner}>
          {t['com.streamlain.auth.sign.plan-banner']({ plan: planDisplayName })}
        </div>
      )}

      <AuthContent>
        <form onSubmit={onSubmit}>
          <AuthInput
            className={style.authInput}
            label={t['com.streamlain.settings.email']()}
            placeholder={t['com.streamlain.auth.sign.email.placeholder']()}
            value={email}
            onChange={setEmail}
            error={!isValidEmail}
            errorHint={
              isValidEmail ? '' : t['com.streamlain.auth.sign.email.error']()
            }
            onEnter={onSignIn}
            autoComplete="email"
          />

          {!isSelfhosted && accessMode === 'gated' && (
            <AuthInput
              className={style.authInput}
              label={t['com.streamlain.auth.access-code.label']()}
              placeholder={
                hasCapturedAccessCode && accessCode.length === 0
                  ? t['com.streamlain.auth.access-code.captured']()
                  : t['com.streamlain.auth.access-code.placeholder']()
              }
              value={accessCode}
              onChange={onAccessCodeChanged}
              autoComplete="off"
            />
          )}

          {/* sl-sc2d: SSO Login Button */}
          {ssoDiscovery?.hasSaml && (
            <RevealButton
              data-testid="sso-login-button"
              label={t['com.streamlain.auth.sign.with-sso']()}
              onClick={onSsoLogin}
            />
          )}

          <div className={style.passwordInputBlock}>
            <AuthInput
              className={style.authInput}
              data-testid="password-input"
              label={t['com.streamlain.auth.password']()}
              value={password}
              type={showPassword ? 'text' : 'password'}
              onChange={setPassword}
              error={passwordError}
              errorHint={t['com.streamlain.auth.password.error']()}
              onEnter={onSignIn}
              autoComplete="current-password"
              endFix={
                <IconButton
                  className={style.passwordVisibilityToggle}
                  size="20"
                  type="button"
                  onClick={onTogglePasswordVisibility}
                  aria-label={
                    showPassword
                      ? t['com.streamlain.auth.password.hide']()
                      : t['com.streamlain.auth.password.show']()
                  }
                  aria-pressed={showPassword}
                >
                  {showPassword ? <InvisibleIcon /> : <ViewIcon />}
                </IconButton>
              }
            />
          </div>

          {!isSelfhosted && (
            <div className={style.passwordButtonRow}>
              <a
                data-testid="forgot-password-link"
                className={style.linkButton}
                onClick={onForgotPassword}
              >
                {t['com.streamlain.auth.sign.forgot-password']()}
              </a>
            </div>
          )}

          {!verifyToken && needCaptcha && <Captcha />}

          {isSelfhosted ? <Back changeState={changeState} /> : null}

          <RevealButton
            data-testid="sign-in-button"
            label={t['com.streamlain.auth.sign.in']()}
            loading={isLoading}
            disabled={isLoading}
            onClick={onSignIn}
            badge={
              lastMethod === 'email'
                ? t['com.streamlain.auth.sign.last-used']()
                : undefined
            }
          />
          <AuthJourneyProgress />
        </form>

        {/* sl-wq59.6.4: Passkey sign-in — renders null when the browser lacks
            WebAuthn support, so we don't need a wrapper conditional here. */}
        <SignInWithPasskey />
        <OAuth redirectUrl={state.redirectUrl} email={email} />

        {/* sl-4g7d.23: Explicit register link → Magic Link flow */}
        {!isSelfhosted && accessMode !== undefined && accessMode !== null && (
          <div className={style.registerLink}>
            <span>{t['com.streamlain.auth.sign.no-account']()}</span>
            <span
              className={style.registerLinkAction}
              data-testid="register-link"
              onClick={onRegister}
            >
              {t['com.streamlain.auth.sign.register']()}
            </span>
          </div>
        )}

        {!isSelfhosted && (
          <div className={style.authMessage}>
            <Trans
              i18nKey="com.streamlain.auth.sign.message"
              components={{
                1: (
                  <a
                    href={legalLinks?.terms?.url ?? ''}
                    target="_blank"
                    rel="noreferrer"
                  />
                ),
                3: (
                  <a
                    href={legalLinks?.privacy?.url ?? ''}
                    target="_blank"
                    rel="noreferrer"
                  />
                ),
              }}
            />
          </div>
        )}
      </AuthContent>
    </AuthContainer>
  );
};
